import { useState, useRef } from "react";
import { Camera, X, Loader2, ImagePlus } from "lucide-react";
import api, { photoUrl } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

export default function PhotoUpload({ photos, setPhotos, multiple = true, testId = "photo-upload" }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const { toast } = useToast();

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        const { data } = await api.post("/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push({ storage_path: data.storage_path, external_url: null });
      }
      setPhotos(multiple ? [...photos, ...uploaded] : uploaded);
    } catch (err) {
      toast({ variant: "destructive", title: "Upload failed", description: "Could not upload photo. Please try again." });
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removePhoto = (idx) => {
    setPhotos(photos.filter((_, i) => i !== idx));
  };

  return (
    <div data-testid={testId} className="space-y-3">
      <div className="flex flex-wrap gap-3">
        {photos.map((p, idx) => (
          <div key={photoUrl(p) || `photo-${idx}`} className="relative w-20 h-20 rounded-xl overflow-hidden border border-amtm-border" data-testid={`${testId}-thumb-${idx}`}>
            <img src={photoUrl(p)} alt="upload" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => removePhoto(idx)}
              data-testid={`${testId}-remove-${idx}`}
              className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5 text-white"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        <label
          htmlFor={`${testId}-input`}
          data-testid={`${testId}-add-button`}
          className="w-20 h-20 rounded-xl border-2 border-dashed border-amtm-teal/50 flex flex-col items-center justify-center gap-1 cursor-pointer active:scale-95 transition-transform bg-amtm-teal/5 min-h-[48px]"
        >
          {uploading ? (
            <Loader2 className="h-5 w-5 animate-spin text-amtm-teal" />
          ) : (
            <>
              <Camera className="h-5 w-5 text-amtm-teal" />
              <span className="text-[10px] text-amtm-teal font-medium">Add Photo</span>
            </>
          )}
        </label>
        <input
          ref={inputRef}
          id={`${testId}-input`}
          type="file"
          accept="image/*"
          multiple={multiple}
          className="hidden"
          onChange={handleFiles}
          disabled={uploading}
        />
      </div>
    </div>
  );
}
